import * as THREE from 'three';

export function closedProfileDistances(profileMm) {
  const distances = [0];
  profileMm.forEach((p, i) => {
    const next = profileMm[(i + 1) % profileMm.length];
    distances.push(distances[i] + Math.hypot(next[0] - p[0], next[1] - p[1]));
  });
  return distances;
}

// By default U wraps the section and V follows the timber length (Pine).
// Textures with horizontal grain, such as Oak, use U along the length. Positions
// are already in metres, so resizing adds grain instead of stretching it.
// Store real mesh UVs: the texture then stays attached when a leaf swings.
export function mapExtrudedWoodUVs(geometry, profilesMm, {
  lengthAxis = 'y', sectionAxes = ['x', 'z'], originMm = [0, 0], signs = [1, 1], repeatsPerMetre = 1, grainAxis = 'v'
} = {}) {
  const segments = profilesMm.flatMap(profile => {
    const distances = closedProfileDistances(profile);
    return profile.flatMap((start, i) => {
      const end = profile[(i + 1) % profile.length];
      const dx = end[0] - start[0], dy = end[1] - start[1], length = distances[i + 1] - distances[i];
      return length > 1e-8 ? [{ start, dx, dy, length, offset: distances[i] }] : [];
    });
  });
  const p = geometry.getAttribute('position'), normal = geometry.getAttribute('normal');
  const axis = { x: 0, y: 1, z: 2 };
  const coordinate = (attribute, index, name) => attribute.getComponent(index, axis[name]);
  const sectionPoint = index => sectionAxes.map((name, j) =>
    (coordinate(p, index, name) * 1000 - originMm[j]) * signs[j]);
  const project = (point, segment) => Math.max(0, Math.min(1,
    ((point[0] - segment.start[0]) * segment.dx + (point[1] - segment.start[1]) * segment.dy) / segment.length ** 2));
  const uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i += 3) {
    const points = [sectionPoint(i), sectionPoint(i + 1), sectionPoint(i + 2)];
    // End cuts, including mitres, need a planar map rather than a collapsed V.
    const endCut = Math.abs(coordinate(normal, i, lengthAxis)) > 0.5;
    let segment;
    let onSection = false;
    if (!endCut) {
      const centre = [0, 1].map(j => (points[0][j] + points[1][j] + points[2][j]) / 3);
      let nearest = Infinity;
      for (const candidate of segments) {
        const t = project(centre, candidate);
        const distance = (centre[0] - candidate.start[0] - t * candidate.dx) ** 2
          + (centre[1] - candidate.start[1] - t * candidate.dy) ** 2;
        if (distance < nearest) { nearest = distance; segment = candidate; }
      }
      onSection = nearest < 0.0001;
    }
    // Boolean threshold cuts can expose new faces inside the original jamb
    // section. Map those along their own cross-section tangent, rather than
    // clamping all vertices to the nearest old corner.
    const sectionNormal = sectionAxes.map((name,j) => coordinate(normal,i,name)*signs[j]);
    const crossLength = Math.hypot(...sectionNormal) || 1;
    points.forEach((point, j) => {
      const across = endCut ? point[0] : onSection ? segment.offset + project(point, segment) * segment.length
        : (point[1]*sectionNormal[0]-point[0]*sectionNormal[1])/crossLength;
      const crossUV = across / 1000 * repeatsPerMetre;
      const lengthUV = (endCut ? point[1] / 1000 : coordinate(p, i + j, lengthAxis)) * repeatsPerMetre;
      uv[(i + j) * 2] = grainAxis === 'u' ? lengthUV : crossUV;
      uv[(i + j) * 2 + 1] = grainAxis === 'u' ? crossUV : lengthUV;
    });
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geometry;
}
